// src/api/subscriptions.ts
// Subscriptions API layer — mirrors mobile apiClient calls from PricingScreen.tsx

import apiClient from '../services/apiClient';

// ─── Types ─────────────────────────────────────────────────────────────────────

export type PortalType = 'property' | 'business';

export interface LivePlan {
  id: string;
  name: string;
  price_monthly: number;
  portal_type: PortalType;
  feature_flags: Record<string, any>;
}

export interface UserSubscription {
  id: string;
  plan_id: string;
  status: string;
  current_period_end?: string;
  unit_count?: number;
  effective_plan?: { name: string };
  subscription_plans?: { name: string };
}

export interface LivePlansResponse {
  property: LivePlan[];
  business: LivePlan[];
}

export interface UserSubscriptionsResponse {
  property: UserSubscription | null;
  business: UserSubscription | null;
}

export interface TrialStatusResponse {
  trialUsed: boolean;
}

export interface StkPushResponse {
  checkout_request_id?: string;
  CheckoutRequestID?: string;
}

export interface PaymentStatusResponse {
  status: 'pending' | 'activated' | 'paid' | 'failed';
  result_desc?: string;
}

export interface ActivateTrialResponse {
  success: boolean;
  message?: string;
  trial_end?: string;
  role_assigned?: string;
  trial_days?: number;
}

// ─── API Calls ─────────────────────────────────────────────────────────────────

export const fetchSubscriptionPlans = (): Promise<LivePlansResponse> =>
  apiClient.get('/subscriptions/plans').then(r => r.data);

export const fetchMySubscriptions = (): Promise<UserSubscriptionsResponse> =>
  apiClient.get('/subscriptions/my-subscriptions').then(r => r.data);

export const fetchTrialStatus = (portal_type: PortalType): Promise<TrialStatusResponse> =>
  apiClient.get(`/subscriptions/trial-status?portal_type=${portal_type}`).then(r => r.data);

export const sendStkPush = (payload: {
  phoneNumber: string;
  amount: number;
  planId: string;
  portal_type: PortalType;
  userId: string;
  unit_count: 1 | 2 | 3;
  planName: string;
}): Promise<StkPushResponse> =>
  apiClient.post('/mpesa/stkpush', payload).then(r => r.data);

export const fetchPaymentStatus = (checkoutId: string): Promise<PaymentStatusResponse> =>
  apiClient.get(`/subscriptions/payment-status/${checkoutId}`).then(r => r.data);

export const activateTrial = (
  portal_type: PortalType,
  gift_code: string
): Promise<ActivateTrialResponse> =>
  apiClient.post('/subscriptions/activate-trial', { portal_type, gift_code }).then(r => r.data);
